
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import useTranslation from 'next-translate/useTranslation';
import { BiographyResponse, Biography, ContributionResponse, ErrorMessage } from '../../../../types/api';
import { getIdFromSlug } from '../../../../lib/api';
import fetcher from '../../../../lib/fetcher';
import useToken from '../../../../hooks/useToken';
import { withToken } from '../../../../contexts/TokenProvider';
import { useNotificationStore } from '../../../../store/notifications';
import Text from '../../../../components/Text';
import ContributeAnnotationForm, { ContributeAnnotationFormState } from '../../../../components/ContributeAnnotationForm';
import Stack from '../../../../components/Stack';
import ContributeLayout from '../../../../components/ContributeLayout';
import SEO from '../../../../components/SEO';
import Icon from '../../../../components/Icon';
import { color, space } from '../../../../stylesheets';
type ContributeAnnotationPageProps = Biography;
const BackLink = styled.button `
  color: ${color('primary')};
  display: inline-flex;
  align-items: center;
  margin-bottom: ${space('m')};

  svg {
    margin-right: ${space('xxs')};
  }

  &:hover span,
  &:focus span {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
`;
const ContributeAnnotationPage: NextPage<ContributeAnnotationPageProps> = ({ name, slug }) => {
    const router = useRouter();
    const addNotification = useNotificationStore(state => state.addNotification);
    const { t } = useTranslation('common');
    const { token, fetchToken } = useToken();
    const handleSubmit = async (state: ContributeAnnotationFormState) => {
        if (!token)
            return;
        const { name, email, annotation, publishName } = state;
        const formData = new FormData();
        formData.append('relatedTo', getIdFromSlug(slug));
        formData.append('name', name);
        formData.append('email', email);
        formData.append('content', annotation);
        formData.append('publishName', publishName.toString());
        try {
            const { data } = await fetcher<ContributionResponse>('/contribution/create/annotations', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            if (data.type === 'success') {
                addNotification({
                    title: t('response.confirm.headline'),
                    text: t('response.confirm.copy'),
                    hideOn: 'close'
                });
                return router.push(`/biography/${slug}`);
            }
            if (data.type === 'review') {
                addNotification({
                    title: t('response.review.headline'),
                    text: t('response.review.copy'),
                    hideOn: 'close'
                });
                return router.push(`/biography/${slug}`);
            }
            addNotification({ text: t('response.general.error') });
        }
        catch (error) {
            fetchToken();
            addNotification({ text: t('response.general.error') });
        }
    };
    return (<>
      <SEO canonical={`/biography/${getIdFromSlug(slug)}/contribute/annotation`} title={t('seo.contribute.title')} description={t('seo.contribute.description', { name })} image="/seo-image-biography.jpg"/>

      <ContributeLayout background="base">
        <BackLink type="button" onClick={() => window.history.back()}>
          <Icon icon="Backward" size="tiny"/>

          <Text as="span" weight="semibold" transform="uppercase">
            {t('labels.back')}
          </Text>
        </BackLink>

        <Stack gap="l">
          <Stack gap="m">
            <Text variant="headline-3" as="h1">
              {t('contribution.annotation.headline', { name })}
            </Text>

            <Text>{t('contribution.annotation.copy', { name })}</Text>
          </Stack>

          <ContributeAnnotationForm onSubmit={handleSubmit}/>
        </Stack>
      </ContributeLayout>
    </>);
};
const getServerSideProps: GetServerSideProps = async ({ query, res, locale }) => {
    const id = getIdFromSlug(query.slug as string);
    const { data } = await fetcher<BiographyResponse>(`/${locale}/biographies/${id}/`);
    /**
     * If no entry was found with given slug
     * return not found page
     */
    if (!data || data.detail === ErrorMessage.NotFound) {
        return {
            props: {},
            notFound: true
        };
    }
    /**
     * If current slug differs from
     * given slug redirect
     */
    if (query.slug !== data.slug) {
        return {
            props: {},
            redirect: {
                permanent: false,
                destination: `/biography/${data.slug}/contribute/annotation`
            }
        };
    }
    /**
     * Otherwise return data to page
     * with cache-control header
     */
    res.setHeader('Cache-Control', 's-maxage=30, stale-while-revalidate=300');
    return { props: data };
};
export default withToken(ContributeAnnotationPage);

    async function __Next_Translate__getServerSideProps__18f80c53008__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/biography/[slug]/contribute/annotation',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__18f80c53008__ as getServerSideProps }
  